import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, X, ChevronRight, BookOpen, Sparkles, Send } from "lucide-react";

// Knowledge base. Each article: id, category, title, keywords (boost search), body (array of paragraphs/steps).
const ARTICLES = [
    {
        id: "hide-columns", category: "Accounts & statements",
        title: "Hide the Prior Year or Budget column",
        keywords: "hide prior year py column budget remove show comparative previous period income statement balance sheet",
        body: [
            "Open the **Accounts** tab for the engagement.",
            "At the very top you'll see a **Columns:** bar. Untick **Budget column** to remove the budget column, or untick **Prior Period** to remove the prior-year column.",
            "This applies to the Income & Expenditure account, the Balance Sheet, the Working TB and the exported PDF.",
            "To hide the prior year in a *specific note table only* (while keeping it on the statements), edit that note, and untick **Show the prior year column in this table** on the table.",
        ],
    },
    {
        id: "rename-columns", category: "Accounts & statements",
        title: "Rename column headings (Current / Prior / Budget)",
        keywords: "rename column heading current period prior budget label caption change wording",
        body: [
            "On the **Accounts** tab, use the **Column headings** boxes in the top toolbar to type your own heading for the Current, Prior and Budget columns.",
            "Click **Reset** next to them to return to the default headings.",
        ],
    },
    {
        id: "rounding", category: "Accounts & statements",
        title: "Round the accounts to whole pounds",
        keywords: "round rounding whole pounds pence decimals rounding account difference",
        body: [
            "On the **Accounts** tab toolbar, tick **Round reports to whole £**. The statements and PDF show whole pounds; the Working TB always keeps the exact pence.",
            "Pick a **Rounding account** so any small rounding difference is absorbed into that account, keeping the statements balanced.",
        ],
    },
    {
        id: "reorder-hide-reports", category: "Accounts & statements",
        title: "Reorder or exclude report sections in the pack",
        keywords: "reorder order sections reports contents hide exclude remove managing agent auditor income statement balance sheet notes layout pack",
        body: [
            "The accounts pack (cover, reports, statements, notes) can be re-ordered and individual sections hidden from the Accounts tab report layout controls.",
            "Hiding a section removes it from both the Contents page and the document body, and the page numbers renumber automatically.",
        ],
    },
    {
        id: "notes-tables", category: "Notes",
        title: "Add several tables to one note",
        keywords: "note multiple tables add table two three four separate schedules duplicate copy reorder drag",
        body: [
            "Edit a note and use **Add another table** to add as many tables as you need. Each table has its own optional heading, rows and Total row.",
            "Use the **copy** icon on a table to duplicate it, and drag the grip handle to reorder tables within the note.",
        ],
    },
    {
        id: "notes-rows", category: "Notes",
        title: "Build a note table (link accounts, formulas, totals)",
        keywords: "note row link account trial balance formula current prior total foot manual line description",
        body: [
            "In a note table, click **+ Add line**, then search and link one or more trial-balance accounts — the current & prior figures pull from the final TB automatically.",
            "Leave an amount blank to use the linked total, type a number to override it, or start with **=** for a formula, e.g. `=current-prior`.",
            "Tick **Show a bold Total row** to foot the lines, and set the total caption (e.g. \"Net movement\").",
            "Drag the grip handle on a row to reorder lines.",
        ],
    },
    {
        id: "fund-note", category: "Notes",
        title: "Edit the Surplus / Deficit (fund movement) note",
        keywords: "surplus deficit reserve fund movement note 8 opening balance schedule rename manual row total",
        body: [
            "The Surplus repayable / Deficit recoverable note has a computed schedule table with an opening + surplus/(deficit) = closing layout and a Total row.",
            "Click **Edit** to rewrite the wording, rename schedule labels and add your own manual rows.",
            "Click **Set opening balances** to enter one-time opening fund balances (these roll forward automatically each year).",
        ],
    },
    {
        id: "tb-import", category: "Trial balance",
        title: "Build or import the trial balance",
        keywords: "trial balance tb import upload csv excel column mapping build create account code name schedule debit credit",
        body: [
            "Open the **TB & mapping** tab. You can add accounts by hand or import a spreadsheet.",
            "On import, map the source columns to Account code, Account name, Schedule, Category, and the Current / Prior / Budget figures (or Debit + Credit).",
            "Choose to replace the whole TB or merge, and import all periods at once or just one (e.g. only Current).",
            "You can inline-edit account names and amounts, add or delete rows, remove duplicates, and clear the whole TB.",
        ],
    },
    {
        id: "tb-map-lead", category: "Trial balance",
        title: "Map the trial balance to lead schedules",
        keywords: "map lead schedule leadsheet assign account tb mapping category classification",
        body: [
            "On the **TB & mapping** tab, assign each account to the relevant lead schedule so it appears on the right working paper.",
            "Accounts with no Income/Expenditure/Asset/Liability classification won't appear in the statements — set the category here.",
        ],
    },
    {
        id: "journals", category: "Working papers",
        title: "Post an adjusting journal",
        keywords: "journal adjustment post debit credit account searchable dropdown narration reference reclass",
        body: [
            "Open a working paper and click **Journal** to raise an adjusting journal.",
            "On each line, use the searchable account box to pick the trial-balance account, then enter the debit or credit. The journal must balance before it can be posted.",
            "Posted journals flow through to the Final TB, the lead schedules (G2), and the accounts automatically.",
        ],
    },
    {
        id: "drill", category: "Working papers",
        title: "See the entries behind a balance (drill-down)",
        keywords: "drill down click balance entries journals prelim final adjustments lead sheet g2 income statement",
        body: [
            "On the Accounts tab, and on any lead schedule or G2, click a balance figure to open a breakdown showing the Preliminary balance, each journal that moved it, and the Final balance.",
        ],
    },
    {
        id: "g2-lead", category: "Working papers",
        title: "Understand the lead schedules and G2",
        keywords: "lead schedule g2 final trial balance prelim adjustments final prior variance accept changes new changed alphabetical",
        body: [
            "Lead schedules show the accounts tested on each working paper; G2 shows the whole final TB with Prelim, Adjustments and Final columns.",
            "Accounts are listed alphabetically. When the TB changes on a re-import, changed accounts are flagged — click **Accept new / changes** to clear the flags once reviewed.",
        ],
    },
    {
        id: "workflow", category: "Getting started",
        title: "The file workflow (what order to do things)",
        keywords: "workflow order process steps overview part a b c d e f g trial balance accounts finalise diagram status",
        body: [
            "The **Overview** tab shows a stepped diagram of the whole process, each step showing its live status (not started / in progress / done):",
            "1) Complete Part A. 2) Build the trial balance. 3) Map it to lead schedules. 4) Work through B–F, posting adjusting journals. 5) Build the Accounts. 6) Return to Part G to finalise.",
        ],
    },
    {
        id: "roll-forward", category: "Getting started",
        title: "Roll a file forward to the next year",
        keywords: "roll forward next year new period opening balances carry forward notes custom lines",
        body: [
            "On the **Accounts** tab, use **Roll forward to next year** to create next year's file. Prior-year closing balances become the new opening balances, and your note customisations, custom lines and renames are preserved.",
        ],
    },
    {
        id: "signoff", category: "Working papers",
        title: "Sign off and review working papers",
        keywords: "sign off review prepared reviewed partner signed complete steps status submit",
        body: [
            "Each working paper has steps to complete and can be marked prepared, then manager-reviewed, then partner-signed.",
            "The Overview cards and the workflow diagram track how far each section has progressed.",
        ],
    },
];

const CATS = ["Getting started", "Trial balance", "Working papers", "Accounts & statements", "Notes"];

function score(article, terms) {
    if (!terms.length) return 0;
    const hay = `${article.title} ${article.title} ${article.keywords} ${article.body.join(" ")}`.toLowerCase();
    let s = 0;
    for (const t of terms) {
        if (!t) continue;
        if (article.title.toLowerCase().includes(t)) s += 6;
        if (article.keywords.toLowerCase().includes(t)) s += 4;
        if (hay.includes(t)) s += 1;
    }
    return s;
}

const render = (p) => {
    // simple **bold** and `code` inline
    const parts = p.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((x, i) => {
        if (x.startsWith("**") && x.endsWith("**")) return <strong key={i}>{x.slice(2, -2)}</strong>;
        if (x.startsWith("`") && x.endsWith("`")) return <code key={i} className="bg-slate-100 px-1 rounded text-[0.85em]">{x.slice(1, -1)}</code>;
        return <span key={i}>{x}</span>;
    });
};

function AskAI() {
    const [q, setQ] = useState("");
    const [answer, setAnswer] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const ask = async () => {
        const question = q.trim();
        if (!question || loading) return;
        setLoading(true); setErr(""); setAnswer("");
        try {
            const { data } = await api.post("/help/ask", { question });
            setAnswer(data.answer || "");
        } catch (e) {
            setErr(e.response?.data?.detail || "The assistant is unavailable — try the search below.");
        } finally { setLoading(false); }
    };
    return (
        <div className="border border-brand/30 bg-brand/5 rounded-sm p-4 mb-6" data-testid="help-ask-ai">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-brand" /><span className="font-display font-semibold text-sm">Ask the assistant</span><span className="text-[11px] text-muted-foreground">— answers questions about using this app</span></div>
            <div className="flex gap-2">
                <input data-testid="help-ask-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }} placeholder="e.g. How do I stop the budget column showing on the balance sheet?" className="flex-1 border border-input rounded-sm px-3 py-2 text-sm bg-white" />
                <button data-testid="help-ask-btn" onClick={ask} disabled={loading} className="flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-sm disabled:opacity-50">{loading ? "Thinking…" : <><Send className="w-3.5 h-3.5" /> Ask</>}</button>
            </div>
            {err && <div className="text-xs text-rose-700 mt-2" data-testid="help-ask-error">{err}</div>}
            {answer && <div className="text-sm bg-white border border-border rounded-sm p-3 mt-3 whitespace-pre-wrap leading-relaxed" data-testid="help-ask-answer">{answer}</div>}
        </div>
    );
}

export default function Help() {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(null);
    const terms = q.trim().toLowerCase().split(/\s+/).filter((t) => t.length > 1 && !["how", "do", "i", "the", "a", "to", "can", "my", "in", "of", "and", "you", "is"].includes(t));
    const results = useMemo(() => {
        if (!terms.length) return null;
        return ARTICLES.map((a) => ({ a, s: score(a, terms) })).filter((x) => x.s > 0).sort((x, y) => y.s - x.s).map((x) => x.a);
    }, [q]); // eslint-disable-line

    const Article = ({ a }) => (
        <div className="border border-border rounded-sm bg-white" data-testid={`help-article-${a.id}`}>
            <button onClick={() => setOpen(open === a.id ? null : a.id)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50">
                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open === a.id ? "rotate-90" : ""}`} />
                <span className="font-display font-semibold text-sm flex-1">{a.title}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.category}</span>
            </button>
            {open === a.id && (
                <div className="px-4 pb-4 pt-1 text-sm text-foreground/90 space-y-2 border-t border-border" data-testid={`help-body-${a.id}`}>
                    {a.body.map((p, i) => <p key={i} className="leading-relaxed">{render(p)}</p>)}
                </div>
            )}
        </div>
    );

    return (
        <div className="p-6 max-w-4xl" data-testid="help-page">
            <div className="flex items-center gap-2 mb-1"><BookOpen className="w-5 h-5 text-brand" /><div className="text-[11px] uppercase tracking-widest text-muted-foreground font-display">Help &amp; user guide</div></div>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-4">How Qube Hub works</h2>

            <AskAI />

            <div className="relative mb-6">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    data-testid="help-search"
                    autoFocus
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(null); }}
                    placeholder="Ask anything, e.g. “how do I hide the prior year column?”"
                    className="w-full border border-input rounded-sm pl-9 pr-9 py-2.5 text-sm bg-white"
                />
                {q && <button data-testid="help-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
            </div>

            {results !== null ? (
                <div className="space-y-2" data-testid="help-results">
                    <div className="text-xs text-muted-foreground mb-2">{results.length} result{results.length === 1 ? "" : "s"} for “{q}”. Click a topic to open it.</div>
                    {results.length === 0 && <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed border-border rounded-sm">No topics matched. Try different words like “prior year”, “journal”, “import”, or “notes”.</div>}
                    {results.map((a) => <Article key={a.id} a={a} />)}
                </div>
            ) : (
                <div className="space-y-6">
                    {CATS.map((cat) => {
                        const list = ARTICLES.filter((a) => a.category === cat);
                        if (!list.length) return null;
                        return (
                            <div key={cat}>
                                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-display mb-2">{cat}</div>
                                <div className="space-y-2">{list.map((a) => <Article key={a.id} a={a} />)}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
